import {
  VESSEL_CARD_ZN_DATE_PATTERN,
  VESSEL_CARD_ZN_FRESHNESS_DAYS,
  VESSEL_CARD_ZN_DAY_MS,
  VESSEL_CARD_ZN_SEGMENTS,
  VESSEL_CARD_ZN_MODES,
} from "../constants/vessel-card-zn.constants";
import type {
  VesselCardZnChartPoint,
  VesselCardZnModelState,
  VesselCardZnResponse,
  VesselCardZnRow,
} from "../models/vessel-card-zn.models";
import type { VesselCardPerformanceRow } from "../models/vessel-card.models";

// Stored-point availability is decided by the backend's exact match and component checks.
// Snowflake source dates do not prove training freshness, so the table policy never applies the old API training gate.
// Explicit fixtures and the parked API contract keep their inclusive 90-day cutoff in UTC.
export const getVesselCardZnModelState = (
  response: VesselCardZnResponse | null,
  today = new Date(),
): VesselCardZnModelState => {
  if (response?.source === "snowflake") return response.modelAvailable ? "available" : "unavailable";

  if (!response?.modelAvailable || !response.trainedAt || !VESSEL_CARD_ZN_DATE_PATTERN.test(response.trainedAt)) {
    return "unavailable";
  }
  const trained = new Date(`${response.trainedAt}T00:00:00Z`);
  if (!Number.isFinite(trained.getTime()) || trained.toISOString().slice(0, 10) !== response.trainedAt)
    return "unavailable";
  const currentDay = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  if (trained > currentDay) return "unavailable";
  return currentDay.getTime() - trained.getTime() > VESSEL_CARD_ZN_FRESHNESS_DAYS * VESSEL_CARD_ZN_DAY_MS
    ? "stale"
    : "available";
};

export const getVesselCardZnSegment = (dwt: number | null) =>
  dwt !== null && Number.isFinite(dwt) && dwt <= 220_000
    ? VESSEL_CARD_ZN_SEGMENTS.find((segment) => dwt >= segment.minimumDwt && dwt < segment.maximumDwt) ?? null
    : null;

export const toVesselCardZnRows = (
  performance: VesselCardPerformanceRow[],
  response: VesselCardZnResponse | null,
  modelState: VesselCardZnModelState,
): VesselCardZnRow[] =>
  VESSEL_CARD_ZN_MODES.map((mode) => {
    const expected = performance.find((row) => row.mode === mode);
    const expectedSpeed = availableMetric(expected?.expectedSpeed);
    const expectedConsumption = availableMetric(expected?.expectedConsumption);
    const prediction = modelState === "available" ? response?.predictions.find((point) => point.mode === mode) : null;
    const speed = availableMetric(prediction?.speed);
    const consumption = availableMetric(prediction?.consumption);
    const comparable = speed !== null && speed > 0 && speed === expectedSpeed && consumption !== null;
    const delta =
      comparable && expectedConsumption !== null && expectedConsumption > 0
        ? ((consumption - expectedConsumption) / expectedConsumption) * 100
        : null;
    return {
      mode,
      expectedSpeed,
      expectedConsumption,
      modelSpeed: comparable ? speed : null,
      modelConsumption: comparable ? consumption : null,
      deltaPercent: delta !== null && Number.isFinite(delta) ? delta : null,
    };
  });

export const formatVesselCardZnDelta = (value: number | null): string => {
  if (value === null || !Number.isFinite(value)) return "—";
  const rounded = Number(value.toFixed(1));
  return `${rounded > 0 ? "+" : ""}${rounded.toFixed(1)}%`;
};

export const getVesselCardZnPoints = (
  rows: VesselCardZnRow[],
  source: "expected" | "model",
): VesselCardZnChartPoint[] =>
  rows
    .flatMap((row) => {
      const speed = source === "expected" ? row.expectedSpeed : row.modelSpeed;
      const consumption = source === "expected" ? row.expectedConsumption : row.modelConsumption;
      return speed !== null && speed > 0 && consumption !== null ? [{ speed, consumption }] : [];
    })
    .sort((left, right) => left.speed - right.speed);

export const getVesselCardZnAxis = (values: number[], minimumPadding: number) => {
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  const padding = Math.max(minimumPadding, (maximum - minimum) * 0.1);
  const start = Math.max(0, minimum - padding);
  const end = maximum + padding;
  return { start, end, ticks: Array.from({ length: 5 }, (_, index) => start + ((end - start) * index) / 4) };
};

const availableMetric = (value: number | null | undefined): number | null =>
  value !== null && value !== undefined && Number.isFinite(value) && value >= 0 ? value : null;
